/* eslint-disable jsx-a11y/no-access-key */
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Input,
  Typography,
} from '@mui/material'
import { Button, useNotify, useTranslate } from 'react-admin'

import { ArtworkApiDTO, CompilationApiDTO } from '../../codegen/generated/Api.schemas'
import {
  useGetApiCompilationsCompilationId,
  usePatchApiCompilationsCompilationId,
} from '../../codegen/generated/compilations'
import { CompilationItemsList } from './CompilationItemsList'

import { Edit } from '@mui/icons-material'
import { CompilationMainArtwork } from './CompilationMainArtwork'
import styles from './Compilations.module.scss'

type EditCompilationFormData = {
  name: string
  mainArtwork: ArtworkApiDTO | undefined
  linkedArtworksIds: ArtworkApiDTO['id'][]
}

export type EditCompilationProps = {
  compilationId: CompilationApiDTO['id']
  compilationName: CompilationApiDTO['name']
  onSuccess: () => void
}

export const EditCompilation = ({
  compilationId,
  compilationName,
  onSuccess,
}: EditCompilationProps): JSX.Element => {
  const notify = useNotify()
  const translate = useTranslate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState<EditCompilationFormData>({
    name: compilationName,
    mainArtwork: undefined,
    linkedArtworksIds: [],
  })

  const compilationQuery = useGetApiCompilationsCompilationId(compilationId, {
    query: {
      enabled: isModalOpen,
    },
  })
  const compilationMutation = usePatchApiCompilationsCompilationId()

  const onEditCompilation = useCallback(async () => {
    try {
      const { name, mainArtwork, linkedArtworksIds } = formData
      if (mainArtwork) {
        await compilationMutation.mutateAsync({
          compilationId,
          data: {
            name,
            mainArtworkId: mainArtwork.id,
            artworks: linkedArtworksIds.map(artworkId => ({
              artworkId,
              compilationShare: (1 / linkedArtworksIds.length).toString(),
            })),
          },
        })

        onSuccess()
        setIsModalOpen(false)
        notify(translate('pages.compilations.editCompilation.toast_editCompilationSuccess'), {
          type: 'success',
        })
      }
    } catch (e: any) {
      console.error(`Error - edit compilation - ${e}`)
      notify(
        `${translate('pages.compilations.editCompilation.toast_editCompilationError')} - ${
          e.response?.data?.message ? e.response.data.message : e
        }`,
        {
          type: 'error',
        },
      )
    }
  }, [
    compilationId,
    compilationMutation.mutateAsync,
    formData,
    notify,
    translate,
    onSuccess,
    setIsModalOpen,
  ])

  const onChange = useCallback((name: keyof typeof formData, value: any) => {
    setFormData(fd => ({ ...fd, [name]: value }))
  }, [])

  const isDisabled = useMemo(
    () =>
      compilationQuery.isLoading ||
      compilationMutation.isLoading ||
      !formData.name ||
      !formData.mainArtwork ||
      formData.linkedArtworksIds.length === 0,
    [formData, compilationMutation.isLoading, compilationQuery.isLoading],
  )

  useEffect(() => {
    if (compilationQuery.data?.body) {
      onChange('mainArtwork', compilationQuery.data?.body.mainArtwork)
    }
  }, [compilationQuery.data?.body.mainArtwork])

  return (
    <>
      <Button
        size="small"
        color="error"
        onClick={() => setIsModalOpen(true)}
        style={{
          minWidth: 'fit-content',
        }}
      >
        <Edit
          color="secondary"
          fontSize="small"
          style={{
            marginLeft: '0.5rem',
          }}
        />
      </Button>
      {isModalOpen && (
        <Dialog
          aria-labelledby="edit-compilation-modal"
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          maxWidth="lg"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle id="edit-compilation-modal">
            {translate('pages.compilations.editCompilation.title')}
          </DialogTitle>
          <DialogContent dividers>
            <div className={styles.message}>
              {compilationQuery.isLoading ? (
                <div className={styles.loader}>
                  <CircularProgress size="2rem" />
                </div>
              ) : (
                <>
                  <Grid container direction="row" spacing={5} marginBottom={5}>
                    <Grid container item xs={3}>
                      <Typography fontWeight="bold">{translate('_generics.name')}</Typography>
                      <FormControl
                        fullWidth
                        sx={{
                          alignSelf: 'flex-end',
                        }}
                      >
                        <Input
                          id="name"
                          name="name"
                          aria-describedby="name"
                          value={formData.name}
                          onChange={e => onChange('name', e.currentTarget.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid container item xs={9} spacing={0}>
                      <Typography fontWeight="bold">
                        {translate('pages.compilations.mainArtwork')}
                      </Typography>
                      <CompilationMainArtwork
                        compilation={compilationQuery.data?.body}
                        onUpdate={mainArtwork => {
                          onChange('mainArtwork', mainArtwork)
                        }}
                      />
                    </Grid>
                  </Grid>
                  <CompilationItemsList
                    compilation={compilationQuery.data?.body}
                    onUpdate={ids => onChange('linkedArtworksIds', ids)}
                  />
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsModalOpen(false)
              }}
              label={translate('_generics.cancel')}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={onEditCompilation}
              label={translate('_generics.edit')}
              disabled={isDisabled}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
